import { useState } from "react";
import { Link } from "react-router-dom";
import { crmApi, formatAED } from "@/lib/crm";
import { useSettings, stageBadgeClass } from "@/contexts/SettingsContext";
import { Search as SearchIcon } from "lucide-react";

export default function Search() {
  const { settings } = useSettings();
  const stages = settings.stages || [];
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await crmApi.search(q.trim());
      setRes(r);
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6">
        <div className="text-[11px] tracking-overline uppercase text-neutral-500">Find anything</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">Search</h1>
      </div>

      <form onSubmit={run} className="mb-8 flex gap-2">
        <div className="relative flex-1">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="search-input" autoFocus placeholder="Search leads, notes, tasks…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-3 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition" />
        </div>
        <button data-testid="search-btn" className="px-5 py-3 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase">Search</button>
      </form>

      {loading && <div className="text-xs tracking-overline uppercase text-neutral-500">Searching…</div>}

      {res && (
        <div className="space-y-8">
          <section>
            <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-3">Leads ({res.leads.length})</div>
            {res.leads.length === 0 ? <div className="text-sm text-neutral-500">No leads matched.</div> :
              <ul className="border border-neutral-200 dark:border-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-800 bg-white dark:bg-neutral-950">
                {res.leads.map((l) => (
                  <li key={l.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <Link to={`/crm/leads/${l.id}`} className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{l.full_name}</div>
                        <div className="text-xs text-neutral-500">{l.email} · {l.service}</div>
                      </div>
                      <span className={`text-[10px] tracking-overline uppercase px-2 py-1 border ${stageBadgeClass(stages.find(x => x.label === l.status)?.color)}`}>{l.status}</span>
                    </Link>
                  </li>
                ))}
              </ul>}
          </section>

          <section>
            <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-3">Notes ({res.notes.length})</div>
            {res.notes.length === 0 ? <div className="text-sm text-neutral-500">No notes matched.</div> :
              <ul className="border border-neutral-200 dark:border-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-800 bg-white dark:bg-neutral-950">
                {res.notes.map((n) => (
                  <li key={n.id} className="p-4">
                    <Link to={`/crm/leads/${n.lead_id}`} className="block">
                      <div className="text-sm">{n.body}</div>
                      <div className="text-xs text-neutral-500 mt-1">{n.author_name}</div>
                    </Link>
                  </li>
                ))}
              </ul>}
          </section>

          <section>
            <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-3">Tasks ({res.tasks.length})</div>
            {res.tasks.length === 0 ? <div className="text-sm text-neutral-500">No tasks matched.</div> :
              <ul className="border border-neutral-200 dark:border-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-800 bg-white dark:bg-neutral-950">
                {res.tasks.map((t) => (
                  <li key={t.id} className="p-4">
                    <Link to={`/crm/leads/${t.lead_id}`} className="block">
                      <div className="text-sm font-medium">{t.title}</div>
                      <div className="text-xs text-neutral-500 mt-1">{t.lead_name || "—"}</div>
                    </Link>
                  </li>
                ))}
              </ul>}
          </section>
        </div>
      )}
    </div>
  );
}
